import { FieldValue, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";
import {
  useAddVendorFinancialInfoMutation,
  useUpdateVendorFinancialInfoMutation,
  useGetVendorFinancialInfoByVendorId,
  useGetPaymentMethodByCommunity,
  useGetPaymentTermsByCommunity,
} from "@/services";
import { routes } from "@/routes/utils";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { schema } from "./validations";
import {
  VENDOR_ID,
  ROUTING_NUMBER,
  ACCOUNT_NUMBER,
  PAYMENT_TERMS,
  PAYMENT_METHOD,
  TAX_CODE,
  TAX_ID,
  STATE_TAX_ID,
  CURRENT_BALANCE,
  PREVIOUS_BALANCE,
  ZELLE_PHONE,
  ZELLE_EMAIL,
} from "./consts";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const useStep2 = () => {
  const { vendorId, id, action } = useParams();
  const location = useLocation();
  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const isMasterData = location?.pathname?.toLowerCase()?.includes("master-data");
  const communityId = isMasterData ? communityData?.communityId : id;
  const vendorListPath = isMasterData
    ? `/${routes.admin}/${routes.masterData}/${routes.vendorMgmt}`
    : `/${routes.admin}/${routes.adminConsole}/${routes.communities}/${id}/${routes.vendorMgmt}`;
  const [keys, setKeys] = useState({
    paymentMethod: Math.random() * 2,
    paymentTerm: Math.random() * 3,
  });

  const form = useForm<any>({
    defaultValues: {
      [ROUTING_NUMBER]: "",
      [ACCOUNT_NUMBER]: "",
      [PAYMENT_TERMS]: "",
      [PAYMENT_METHOD]: "",
      [TAX_CODE]: "",
      [TAX_ID]: "",
      [STATE_TAX_ID]: "",
      [CURRENT_BALANCE]: "",
      [PREVIOUS_BALANCE]: "",
      [ZELLE_PHONE]: "",
      [ZELLE_EMAIL]: "",
      [VENDOR_ID]: vendorId,
    },
    resolver: zodResolver(schema),
  });

  const navigate = useNavigate();
  const isView = action === "view";

  const requiredFields = getRequiredFields(schema);

  const { data: details, isLoading: isVendorDtlLoading } =
    useGetVendorFinancialInfoByVendorId({
      id: vendorId as string,
      queryParams: {
        enabled: !!vendorId,
      },
    });

  const { data: paymentMethodList, isLoading: isPaymentMethodLoading } =
    useGetPaymentMethodByCommunity({communityId: communityId as string});

  const { data: paymentTermList, isLoading: isPaymentTermLoading } =
    useGetPaymentTermsByCommunity({communityId: communityId as string});

  useEffect(() => {
    if (details) {
      form.reset({
        [ROUTING_NUMBER]: details?.[ROUTING_NUMBER]?.toString() || "",
        [ACCOUNT_NUMBER]: details?.[ACCOUNT_NUMBER]?.toString() || "",
        [PAYMENT_TERMS]: details?.[PAYMENT_TERMS]?.toString() || "",
        [PAYMENT_METHOD]: details?.[PAYMENT_METHOD]?.toString() || "",
        [TAX_CODE]: details?.[TAX_CODE]?.toString() || "",
        [TAX_ID]: details?.[TAX_ID]?.toString() || "",
        [STATE_TAX_ID]: details?.[STATE_TAX_ID]?.toString() || "",
        [CURRENT_BALANCE]: details?.[CURRENT_BALANCE]?.toString() || "",
        [PREVIOUS_BALANCE]: details?.[PREVIOUS_BALANCE]?.toString() || "",
        [ZELLE_PHONE]: details?.[ZELLE_PHONE]?.toString() || "",
        [ZELLE_EMAIL]: details?.[ZELLE_EMAIL] || "",
        [VENDOR_ID]: vendorId,
      });
      setKeys({
        paymentMethod: Math.random() * 2,
        paymentTerm: Math.random() * 3,
      });
    }
  }, [details]);

  useEffect(() => {
    if (paymentMethodList) {
      setKeys((prev) => ({ ...prev, paymentMethod: Math.random() * 2 }));
    }
  }, [paymentMethodList]);

  const { mutate: addInfo, isPending: isAddPending } =
    useAddVendorFinancialInfoMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        navigate(vendorListPath);
        toast.success("Financial Info Added Successfully");
      },
    });

  // update property
  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateVendorFinancialInfoMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        navigate(vendorListPath);
        toast.success("Financial Info Updated Successfully");
      },
    });

  const onSubmit = (data: FieldValue<any>) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (details) {
      updateInfo({
        vendorId: vendorId as string,
        data: {
          ..._data,
        },
      });
    } else {
      addInfo({
        vendorId: vendorId as string,
        data: { ..._data },
      });
    }
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isAddPending,
    isUpdatePending,
    isView,
    vendorId,
    isVendorDtlLoading,
    paymentMethodList,
    isPaymentMethodLoading,
    keys,
    paymentTermList,
    isPaymentTermLoading,
  };
};

export default useStep2;
